import React, { useState } from "react"
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Space,
  Button,
  Tooltip,
  Typography,
} from "antd"
import { InfoCircleFilled, UserAddOutlined } from "@ant-design/icons"
import { addDoc, collection, doc, getDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../../firebase/config"
import { useAuthContext } from "../../hooks/useAuthContext"
import { useCollection } from "../../hooks/useCollection"
import { Notification } from "../Notification"
import { CountryCodesSelect } from "./CountryCodesSelect"

const { Text } = Typography
const { Option } = Select

export default function CreateTapeClient({ icon }) {
  const { user } = useAuthContext()
  const { data: groups } = useCollection(
    "clientGroups",
    ["user", "==", user.uid],
    ["approved", "==", true]
  )
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const isMpUser = user.uid === "C6eD2XCyx5Vi7ANQwqlwdb0Q9Hf2"

  const open = () => setVisible(true)
  const close = () => {
    form.resetFields()
    setVisible(false)
  }

  const onFinish = async (vals) => {
    setLoading(true)
    try {
      // 1️⃣ fetch the manufacturer doc
      const manRef = doc(db, "manufacturers", "DodjV1wBRUlrKpmLPgRe")
      const manSnap = await getDoc(manRef)
      const manufacturer = manSnap.exists() ? { id: manSnap.id, ...manSnap.data() } : null

      // 2️⃣ pull those three that live outside Form.Items
      const country = form.getFieldValue("country")
      const countryCode = form.getFieldValue("countryCode")
      const phoneEx = form.getFieldValue("phoneEx")

      // 3️⃣ destructure everything else
      const {
        client,
        vat,
        reg,
        accountant,
        accountantEmail,
        accountantNumber,
        contactEmail,
        contactName,
        contactNumber,
        deliveryAddress,
        deliveryAddress2,
        city,
        street,
        postalCode,
        profitability,
        clientGroup,
      } = vals

      // 4️⃣ build your payload, null-filling any undefined fields
      const payload = {
        client, // required
        vat: countryCode + vat, // required
        reg, // required
        country, // always set from form
        accountant: accountant ?? null,
        accountantEmail: accountantEmail ?? null,
        accountantNumber: accountantNumber ?? null,
        contactEmail: contactEmail ?? null,
        contactName: contactName ?? null,
        contactNumber: phoneEx + (contactNumber ?? ""),
        deliveryAddress: deliveryAddress ?? null,
        deliveryAddress2: deliveryAddress2 ?? null,
        city: city ?? null,
        street: street ?? null,
        postalCode: postalCode ?? null,
        profitability: profitability ?? 0,
        clientGroup: clientGroup ?? null,
        manufacturer, // your fetched object or null
        user: user.uid,
        userEmail: user.email,
        approved: false,
        tapeClient: true,
        creditLimit: 9000,
        creditUsed: 0,
        creditLeft: 9000,
        orderCredit: 0,
        accountNumber: null,
        bankCode: null,
        bankName: null,
        date: {
          timestamp: serverTimestamp(),
          createdAt: serverTimestamp(),
          lastEdit: serverTimestamp(),
        },
        status: {
          clientStatus: 0,
          created: serverTimestamp(),
          approved: null,
          ordered: null,
        },
      }

      // 5️⃣ write it
      await addDoc(collection(db, "clients"), payload)

      Notification("success", "Client Added", "You have successfully added a new client!")
      close()
    } catch (err) {
      Notification("error", "Creation Error", err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button
        type="primary"
        icon={icon ? <UserAddOutlined style={{ color: "white" }} /> : null}
        onClick={open}>
        {!icon && "Create Tape Client"}
      </Button>

      <Modal
        title="Create Tape Client"
        open={visible}
        onCancel={close}
        onOk={() => form.submit()}
        okText="Create Tape Client"
        okButtonProps={{ loading }}
        width={1000}>
        <Form
          form={form}
          style={{ width: "100%" }}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            country: "Latvia",
            countryCode: "LV",
            phoneEx: "+371",
            hasGroup: false,
            profitability: 0,
          }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            {/* === first row of eight cols === */}
            <Row gutter={[16, 15]}>
              <Col span={8}>
                <Text>Has Client Group?</Text>
                <Input.Group compact>
                  {/* toggles the boolean */}
                  <Form.Item name="hasGroup" noStyle>
                    <Select style={{ width: "25%" }}>
                      <Option value={false}>No</Option>
                      <Option value={true}>Yes</Option>
                    </Select>
                  </Form.Item>

                  {/* re‑renders when hasGroup flips */}
                  <Form.Item noStyle shouldUpdate={(prev, curr) => prev.hasGroup !== curr.hasGroup}>
                    {({ getFieldValue }) => (
                      <Select
                        showSearch
                        disabled={!getFieldValue("hasGroup")}
                        style={{ width: "75%" }}
                        placeholder={getFieldValue("hasGroup") ? "Select group" : "Disabled"}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children.toLowerCase().includes(input.toLowerCase())
                        }
                        filterSort={(a, b) =>
                          a.children.toLowerCase().localeCompare(b.children.toLowerCase())
                        }
                        onSelect={(_, opt) =>
                          form.setFieldsValue({ profitability: opt.props.profitability })
                        }>
                        {groups?.map((g) => (
                          <Option key={g.id} value={g.groupName} profitability={g.profitability}>
                            {g.groupName}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Input.Group>
              </Col>

              <Col span={8}>
                <Space>
                  <Text>Company Name</Text>
                  <Text italic style={{ color: "red", fontSize: 10 }}>
                    (required)
                  </Text>
                </Space>
                <Form.Item
                  name="client"
                  rules={[{ required: true, message: "Required" }]}
                  style={{ margin: 0 }}>
                  <Input placeholder="Staburadze" />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Text>Client’s Country</Text>
                <Form.Item noStyle>
                  <CountryCodesSelect
                    defaultCountry={form.getFieldValue("country")}
                    setSelectedCountry={(c) => form.setFieldsValue({ country: c })}
                    setSelectedCountryCode={(cc) => form.setFieldsValue({ countryCode: cc })}
                    setSelectedPhoneEx={(ex) => form.setFieldsValue({ phoneEx: ex })}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Space>
                  <Text>Reg. Number</Text>
                  <Text italic style={{ color: "red", fontSize: 10 }}>
                    (required)
                  </Text>
                </Space>
                <Form.Item
                  name="reg"
                  rules={[{ required: true, message: "Required" }]}
                  style={{ margin: 0 }}>
                  <Input placeholder="40103217882" />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Space size="small">
                  <Text>VAT Number</Text>
                  <Text italic style={{ color: "red", fontSize: 10 }}>
                    (required)
                  </Text>
                </Space>
                <Form.Item
                  name="vat"
                  rules={[{ required: true, message: "Required" }]}
                  style={{ margin: 0 }}>
                  <Input
                    addonBefore={form.getFieldValue("countryCode")}
                    placeholder="40103217882"
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Text>Contact Email</Text>
                <Form.Item name="contactEmail" style={{ margin: 0 }}>
                  <Input placeholder="anna@staburadze.lv" />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Text>Contact Number</Text>
                <Input.Group compact>
                  <Form.Item name="phoneEx" noStyle>
                    <Input style={{ width: "25%" }} />
                  </Form.Item>
                  <Form.Item name="contactNumber" noStyle>
                    <Input style={{ width: "75%" }} placeholder="67080302" />
                  </Form.Item>
                </Input.Group>
              </Col>

              <Col span={8}>
                <Text>Contact Name</Text>
                <Form.Item name="contactName" style={{ margin: 0 }}>
                  <Input placeholder="Anna Staburadze" />
                </Form.Item>
              </Col>
            </Row>

            {/* === second row === */}
            <Row gutter={[16, 10]}>
              <Col span={8}>
                <Text>City</Text>
                <Form.Item name="city" style={{ margin: 0 }}>
                  <Input placeholder="Riga" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Text>Street Address</Text>
                <Form.Item name="street" style={{ margin: 0 }}>
                  <Input placeholder="Miera street 22" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Text>Postal Code</Text>
                <Form.Item name="postalCode" style={{ margin: 0 }}>
                  <Input placeholder="LV-1001" />
                </Form.Item>
              </Col>
            </Row>

            {/* === MP‑only profit % === */}
            {isMpUser && (
              <Row gutter={[16, 10]}>
                <Col span={8}>
                  <Space>
                    <Tooltip title="This is a client percent. If your client has no group, you can input this percent, otherwise upon selecting your group, the percent will show up!">
                      <InfoCircleFilled />
                    </Tooltip>
                    <Text>Client’s Profit</Text>
                    <Text italic style={{ color: "red", fontSize: 10 }}>
                      (required)
                    </Text>
                  </Space>
                  <Form.Item
                    name="profitability"
                    rules={[{ required: true, message: "Required" }]}
                    style={{ margin: 0 }}>
                    <InputNumber
                      min={0}
                      max={100}
                      formatter={(v) => `${v}%`}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {/* === error row (handled by Form.Item rules) === */}
          </Space>
        </Form>
      </Modal>
    </>
  )
}
